import { useCallback, useEffect, useState } from 'react';
import { getStoredCredentials } from '../utils/auth';
import { getPublicKey, subscribePush, unsubscribePush } from '../services/push';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const output = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) {
        output[i] = rawData.charCodeAt(i);
    }
    return output;
}

function errorToMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'object' && err !== null) {
        const o = err as Record<string, unknown>;
        if (typeof o.message === 'string') return o.message;
        if (typeof o.code === 'string') return o.code;
    }
    return "Une erreur s'est produite.";
}

export const usePushNotifications = () => {
    const [supported, setSupported] = useState(true);
    const [permission, setPermission] = useState<NotificationPermission>('default');
    const [subscription, setSubscription] = useState<PushSubscription | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (
            typeof window === 'undefined' ||
            !('serviceWorker' in navigator) ||
            !('PushManager' in window) ||
            !('Notification' in window)
        ) {
            setSupported(false);
            return;
        }
        setSupported(true);
        setPermission(Notification.permission);

        let cancelled = false;
        navigator.serviceWorker.ready
            .then((reg) => reg.pushManager.getSubscription())
            .then((sub) => {
                if (!cancelled) setSubscription(sub);
            })
            .catch(() => {
                if (!cancelled) setSubscription(null);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const subscribe = useCallback(async () => {
        const credentials = getStoredCredentials();
        if (!credentials?.user_login || !credentials?.password) {
            setError('Vous devez être connecté pour activer les notifications.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const perm = await Notification.requestPermission();
            setPermission(perm);
            if (perm !== 'granted') {
                return;
            }

            const { publicKey } = await getPublicKey();
            const reg = await navigator.serviceWorker.ready;
            const applicationServerKey = urlBase64ToUint8Array(publicKey);
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey,
            });

            try {
                await subscribePush(credentials, sub.toJSON());
            } catch (apiErr) {
                await sub.unsubscribe().catch(() => {});
                throw apiErr;
            }

            setSubscription(sub);
        } catch (err) {
            setError(errorToMessage(err));
            console.error('subscribe push failed:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const unsubscribe = useCallback(async () => {
        setError(null);

        let reg: ServiceWorkerRegistration;
        try {
            reg = await navigator.serviceWorker.ready;
        } catch {
            setSubscription(null);
            return;
        }

        let sub: PushSubscription | null = null;
        try {
            sub = await reg.pushManager.getSubscription();
        } catch {
            setSubscription(null);
            return;
        }

        if (!sub) {
            setSubscription(null);
            return;
        }

        setLoading(true);

        try {
            const credentials = getStoredCredentials();
            if (credentials?.user_login && credentials?.password) {
                try {
                    await unsubscribePush(credentials, sub.toJSON());
                } catch (e) {
                    console.warn('unsubscribePush API failed:', e);
                }
            }
            await sub.unsubscribe();
            setSubscription(null);
        } catch (err) {
            setError(errorToMessage(err));
            console.error('unsubscribe push failed:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        supported,
        permission,
        subscription,
        loading,
        error,
        subscribe,
        unsubscribe,
        clearError,
    };
};
